import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef } from "react";
import helpIcon from "@ingka/ssr-icon/paths/question-mark";
import SSRIcon from "@ingka/ssr-icon";
import { durationQuickWeb } from "@ingka/variables";
import { string, oneOfType, arrayOf, node, bool, func, oneOf, object } from "prop-types";
import cx from "classnames";
import TooltipService, { resolveOverflow, HOVER_DELAY, POSITIONS } from "@ingka/tooltip-shared";
const duration = durationQuickWeb && durationQuickWeb.slice(0, -2);
const propTypes = {
  tooltipText: string.isRequired,
  className: string,
  prefix: string,
  ssrIcon: func,
  children: oneOfType([arrayOf(node), node]),
  position: oneOf(["top", "trailing", "bottom", "leading"]),
  toggle: bool,
  describedById: string,
  tooltipButtonLabel: string,
  interactiveCustomTrigger: bool,
  triggerWithText: bool,
  onClick: func,
  boundaryContainerID: string,
  controlProps: object
};
const Tooltip = (props) => {
  const {
    prefix = "",
    className,
    ssrIcon,
    children,
    tooltipText,
    toggle = false,
    describedById,
    tooltipButtonLabel,
    interactiveCustomTrigger,
    triggerWithText,
    position = "top",
    onClick,
    boundaryContainerID,
    controlProps
  } = props;
  const tooltipEl = useRef(null);
  const tooltipBodyEl = useRef(null);
  const closingTimeoutRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const resizeDebounceTimeoutRef = useRef(null);
  const [initialized, setInitialized] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [opacityAnimationActive, setOpacityAnimationActive] = useState(false);
  const [instantlyOpened, setInstantlyOpened] = useState(false);
  const horizontal = position === POSITIONS.LEADING || position === POSITIONS.TRAILING;
  const handleKeyUp = (e) => {
    if (e.key === "Tab") {
      handleMouseEnter(true);
    }
  };
  const handleKeyDown = (e) => {
    if (e.key === "Tab" || e.key === "Escape") {
      handleMouseLeave();
    }
  };
  const handleMouseEnter = (triggeredByKeyboard) => {
    resolveOverflow(tooltipEl.current, tooltipBodyEl.current, boundaryContainerID);
    clearTimeout(hoverTimeoutRef.current);
    const delay = TooltipService.preventTransition ? 0 : HOVER_DELAY;
    if (!toggle) {
      setInstantlyOpened(TooltipService.preventTransition);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setIsOpen(true);
    }, delay);
  };
  const handleMouseLeave = () => {
    TooltipService.setTooltipGroupTimer();
    clearTimeout(hoverTimeoutRef.current);
    handleClosing();
  };
  const handleToggle = (e) => {
    if (!horizontal) {
      resolveOverflow(tooltipEl.current, tooltipBodyEl.current, boundaryContainerID);
    }
    if (!isOpen) {
      setIsOpen(true);
    } else {
      handleClosing();
    }
    if (onClick)
      onClick(e, !isOpen);
  };
  const handleEscapeKey = (e) => {
    if (e.key === "Escape") {
      handleClosing();
    }
  };
  const handleClosing = () => {
    setIsOpen(false);
    setOpacityAnimationActive(true);
    setInstantlyOpened(!toggle);
  };
  useEffect(
    () => {
      clearTimeout(closingTimeoutRef.current);
      closingTimeoutRef.current = setTimeout(() => {
        setOpacityAnimationActive(false);
      }, duration);
    },
    [opacityAnimationActive]
  );
  const handleResize = () => {
    if (!horizontal) {
      resolveOverflow(tooltipEl.current, tooltipBodyEl.current, boundaryContainerID);
    }
    clearTimeout(resizeDebounceTimeoutRef.current);
    resizeDebounceTimeoutRef.current = setTimeout(() => {
      resolveOverflow(tooltipEl.current, tooltipBodyEl.current, boundaryContainerID);
    }, 100);
  };
  useEffect(() => {
    setInitialized(true);
    if (!horizontal) {
      resolveOverflow(tooltipEl.current, tooltipBodyEl.current, boundaryContainerID);
    }
    window.addEventListener("keydown", handleEscapeKey);
    window.addEventListener("resize", handleResize);
    return function cleanup() {
      clearTimeout(closingTimeoutRef.current);
      clearTimeout(hoverTimeoutRef.current);
      clearTimeout(resizeDebounceTimeoutRef.current);
      window.removeEventListener("keydown", handleEscapeKey);
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  const baseClass = `${prefix}tooltip`;
  const wrapperClasses = cx(
    className,
    baseClass,
    position === POSITIONS.TOP && `${baseClass}--top`,
    position === POSITIONS.TRAILING && `${baseClass}--trailing`,
    position === POSITIONS.BOTTOM && `${baseClass}--bottom`,
    position === POSITIONS.LEADING && `${baseClass}--leading`,
    toggle && `${baseClass}--toggle`
  );
  const bodyClasses = cx(
    `${baseClass}__body`,
    !initialized && `${baseClass}__body--hidden`,
    isOpen && `${baseClass}__body--entering`,
    opacityAnimationActive && !instantlyOpened && `${baseClass}__body--leaving`,
    instantlyOpened && !toggle && `${baseClass}__body--no-transition`
  );
  const customTriggerWrapperClasses = cx(
    `${baseClass}__custom-trigger-wrapper`
  );
  const triggerButtonClasses = cx(
    `${baseClass}__trigger-button`,
    isOpen && toggle && `${baseClass}__trigger-button--active`
  );
  const childClone = React.Children.toArray(children).slice(0, 1).map((child) => {
    return React.cloneElement(child, { "aria-describedby": describedById });
  });
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: wrapperClasses,
      ref: tooltipEl,
      onMouseEnter: !toggle ? handleMouseEnter : null,
      onMouseLeave: !toggle ? handleMouseLeave : null,
      onFocus: handleMouseEnter,
      onBlur: !toggle ? handleMouseLeave : null,
      children: children && !toggle ? /* @__PURE__ */ jsxs(
        "span",
        {
          className: customTriggerWrapperClasses,
          "aria-describedby": interactiveCustomTrigger ? null : describedById,
          tabIndex: interactiveCustomTrigger ? null : 0,
          onKeyUp: handleKeyUp,
          onKeyDown: handleKeyDown,
          ...controlProps,
          children: [
            childClone,
            /* @__PURE__ */ jsx("span", { className: bodyClasses, ref: tooltipBodyEl, children: /* @__PURE__ */ jsx("span", { className: `${baseClass}__message-background`, role: "tooltip", id: describedById, children: tooltipText }) })
          ]
        }
      ) : /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs(
          "button",
          {
            className: triggerButtonClasses,
            "aria-pressed": toggle ? isOpen : null,
            onClick: toggle ? handleToggle : null,
            onKeyUp: !toggle ? handleKeyUp : null,
            onKeyDown: !toggle ? handleKeyDown : null,
            "aria-describedby": describedById,
            ...controlProps,
            children: [
              /* @__PURE__ */ jsx(SSRIcon, { prefix, paths: ssrIcon || helpIcon }),
              tooltipButtonLabel && /* @__PURE__ */ jsx("span", { className: `${baseClass}__trigger-button-label`, children: tooltipButtonLabel })
            ]
          }
        ),
        /* @__PURE__ */ jsx("span", { className: bodyClasses, ref: tooltipBodyEl, children: /* @__PURE__ */ jsx("span", { className: `${baseClass}__message-background`, role: "tooltip", id: describedById, children: tooltipText }) })
      ] })
    }
  );
};
Tooltip.propTypes = propTypes;
var Tooltip_default = Tooltip;
export {
  Tooltip_default as default
};
